import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { FC } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { colors, gutterSize } from '../../../theme';
import {
    HorizontalBarChartStyled,
    HorizontalBarComparisonCellStyled,
    HorizontalBarComparisonColumnStyled,
    HorizontalBarFooterStyled,
    HorizontalBarIndicatorStyled,
    HorizontalBarRowStyled,
} from '../styled';
import { evolutionIconPlugin, getEvolution, withEvolutionMarker } from './evolution';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
    ChartDataLabels,
);

enum dataLabelAlignAnchorType {
    END = 'end',
}

enum dataLabelWeight {
    BOLD = 'bold'
}

const MINIMUM_CHART_HEIGHT = 80;
const ROW_HEIGHT = 55;
const LABEL_FONT_FAMILY = 'Barlow';
const ELLIPSIS = '…';

const getLabelFontSize = (width: number) => Math.round(width / (width * 0.06));

// Shortens the label to what fits on the canvas, the tooltip still shows it in full.
const truncateLabel = (ctx: CanvasRenderingContext2D, label: string, maxWidth: number, fontSize: number) => {
    ctx.save();
    ctx.font = `${fontSize}px ${LABEL_FONT_FAMILY}`;
    let text = label;
    if (ctx.measureText(text).width > maxWidth) {
        while (text.length > 0 && ctx.measureText(`${text}${ELLIPSIS}`).width > maxWidth) {
            text = text.slice(0, -1);
        }
        text = `${text.trimEnd()}${ELLIPSIS}`;
    }
    ctx.restore();

    return text;
};

// Chart.js calls the tick callback with the scale as `this`, which gives the canvas and its width.
const createTickCallback = (labels: string[]) => function tickCallback(this: any, value: any) {
    const label = labels[value];
    if (label == null) {
        return '';
    }
    const { width } = this.chart;
    const maxWidth = width - (gutterSize.s * 2);

    return truncateLabel(this.ctx, label, maxWidth, getLabelFontSize(width));
};

const TOOLTIP_TITLE_FONT = { size: 12, weight: 'bold' as const, family: LABEL_FONT_FAMILY };
// Tooltip padding and caret, so the box itself stays inside the canvas.
const TOOLTIP_HORIZONTAL_MARGIN = 40;

// Chart.js never wraps tooltip text, a long title runs past the canvas and gets cut.
// The title is split into lines that fit, word by word, and a word too long for a line is broken.
const wrapTooltipTitle = (ctx: CanvasRenderingContext2D, label: string, maxWidth: number) => {
    ctx.save();
    ctx.font = `${TOOLTIP_TITLE_FONT.weight} ${TOOLTIP_TITLE_FONT.size}px ${TOOLTIP_TITLE_FONT.family}`;
    const lines: string[] = [];
    let currentLine = '';
    label.split(' ').forEach((word) => {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth) {
            currentLine = candidate;

            return;
        }
        if (currentLine) {
            lines.push(currentLine);
        }
        let remaining = word;
        while (ctx.measureText(remaining).width > maxWidth && remaining.length > 1) {
            let cut = remaining.length - 1;
            while (cut > 1 && ctx.measureText(remaining.slice(0, cut)).width > maxWidth) {
                cut -= 1;
            }
            lines.push(remaining.slice(0, cut));
            remaining = remaining.slice(cut);
        }
        currentLine = remaining;
    });
    if (currentLine) {
        lines.push(currentLine);
    }
    ctx.restore();

    return lines;
};

type HorizontalBarIndicatorProps = {
    listOfValues: any
}

const HorizontalBarIndicator: FC<HorizontalBarIndicatorProps> = ({ listOfValues }) => {
    const { t } = useTranslation('dashboard');
    const sortedData = Object.entries(listOfValues.data)
        .sort((a, b) => {
            const rankA = listOfValues.ranking[a[0]];
            const rankB = listOfValues.ranking[b[0]];

            return rankA - rankB;
        });
    const sortedColors = Object.entries(listOfValues.colors)
        .sort((a, b) => {
            const rankA = listOfValues.ranking[a[0]];
            const rankB = listOfValues.ranking[b[0]];

            return rankA - rankB;
        });

    const labels = sortedData.map(([key]) => key);
    const backgroundColors = sortedColors.map(([key, value]) => key && value as string);
    const evolutions = labels.map((label: string) => getEvolution(
        listOfValues.comparison?.type,
        listOfValues.comparison?.results?.data?.significance,
        label,
    ));
    const formatValue = (value: number, index: number) => withEvolutionMarker(`${Number(value).toFixed(listOfValues.round)}%`, evolutions[index]);
    const options = {
        maintainAspectRatio: false,
        indexAxis: 'y' as const,
        responsive: true,
        layout: {
            padding: {
                left: gutterSize.s,
                right: gutterSize.xxxl,
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                displayColors: false,
                titleFont: TOOLTIP_TITLE_FONT,
                callbacks: {
                    title: (items: any[]) => {
                        if (items.length === 0) {
                            return '';
                        }
                        const { chart, label } = items[0];

                        return wrapTooltipTitle(chart.ctx, label, chart.width - TOOLTIP_HORIZONTAL_MARGIN);
                    },
                    // Same formatting as the value drawn at the end of the bar.
                    label: (dataset: any) => formatValue(dataset.raw, dataset.dataIndex),
                },
            },
        },
        scales: {
            x: {
                display: false,
            },
            y: {
                display: true,
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    autoSkip: false,
                    labelOffset: -25,
                    mirror: true,
                    padding: 0,
                    font: (context: any) => ({
                        color: colors.grey200,
                        size: getLabelFontSize(context.chart.width),
                        family: LABEL_FONT_FAMILY,
                    }),
                    callback: createTickCallback(labels),
                },
            },
        },
    };

    const values = sortedData.map(([, value]) => value);

    const data = {
        labels,
        datasets: [{
            maxBarThickness: 15,
            data: values,
            backgroundColor: backgroundColors,
            borderRadius: 50,
            borderSkipped: false,
            datalabels: {
                align: dataLabelAlignAnchorType.END,
                display: true,
                anchor: dataLabelAlignAnchorType.END,
                formatter: (value: number, context: any) => formatValue(value, context.dataIndex),
                font: (context: any) => {
                    const { width } = context.chart;
                    const size = Math.round(width / (width * 0.065));

                    return {
                        size,
                        weight: dataLabelWeight.BOLD,
                        family: LABEL_FONT_FAMILY,
                    };
                },
            },
        }],
    };

    const comparedValues = listOfValues.comparison?.results?.data?.data;
    const isComparisonReadable = Boolean(comparedValues) && listOfValues.base > 0;
    const chartHeight = Math.max(MINIMUM_CHART_HEIGHT, labels.length * ROW_HEIGHT);
    const isTemporalComparison = listOfValues.comparison?.type === 'TEMPORAL';
    const hasNoComparedData = isComparisonReadable && (
        listOfValues.comparison?.base === 0 || labels.every((label: string) => comparedValues[label] == null)
    );
    let comparisonHeader = isTemporalComparison ? t('compareToGlobal.temporalColumnHeader') : t('compareToGlobal.columnHeader');
    if (hasNoComparedData) {
        comparisonHeader = isTemporalComparison ? t('compareToGlobal.temporalNoData') : t('compareToGlobal.noData');
    }

    return (
        <HorizontalBarIndicatorStyled>
            {isComparisonReadable && (
                <HorizontalBarFooterStyled>
                    <HorizontalBarComparisonColumnStyled>
                        {comparisonHeader}
                    </HorizontalBarComparisonColumnStyled>
                </HorizontalBarFooterStyled>
            )}
            <HorizontalBarRowStyled>
                <HorizontalBarChartStyled $height={chartHeight}>
                    <Bar
                        data={data}
                        options={options}
                        plugins={[evolutionIconPlugin]}
                        style={{
                            breakInside: 'avoid',
                            breakAfter: 'avoid',
                        }}
                    />
                </HorizontalBarChartStyled>
                {isComparisonReadable && (
                    <HorizontalBarComparisonColumnStyled>
                        {labels.map((label: string) => (
                            <HorizontalBarComparisonCellStyled key={label} $height={chartHeight / labels.length}>
                                {comparedValues[label] == null ? '-' : `${Number(comparedValues[label]).toFixed(listOfValues.round)}%`}
                            </HorizontalBarComparisonCellStyled>
                        ))}
                    </HorizontalBarComparisonColumnStyled>
                )}
            </HorizontalBarRowStyled>
        </HorizontalBarIndicatorStyled>
    );
};

export default HorizontalBarIndicator;
